// src/portal/PortalRollupCard.jsx
//
// PORTED from the mock's `.dash-card` — the rollup headline, the progress bar,
// the four cells and the queue preview.
//
// ── ONE CARD, BOTH ROLES ───────────────────────────────────────────────────
//
// The mock only draws the owner's side. The operator's side is not a second
// design: the same card, with the copy that differs coming in as props from the
// page, which already knows the role from route 5's `scope`. Five defects in
// this feature were a thing built for the surface being tested and absent on
// the other, and a second card would be the sixth by construction.
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { rollupCells } from "./portalRollup";

const TONE = {
  due: "text-accent-action",
  ok: "text-status-ok",
  idle: "text-fg-primary",
};

/** The queue preview shows at most this many rows; the rest become a count. */
export const QUEUE_PREVIEW = 3;

export default function PortalRollupCard({
  rollup, awaiting, totalAwaiting, title, blurb, emptyLine, stamp, openTo,
}) {
  const cells = rollupCells(rollup);
  const more = Math.max(0, totalAwaiting - awaiting.length);

  return (
    <section className="rounded-xl border border-bg-border bg-bg-soft overflow-hidden">
      {/* ── THE MOCK'S .dash-card__flag, AND ORANGE'S ONLY HOME IN CHROME ───
          2px × 84px of brand at the top edge. The mock spends its accent here
          and essentially nowhere else in chrome, which is the whole argument
          for orange leaving the 141 active sites: it is a MARK, not a state.
          It carries no meaning, so it is aria-hidden. */}
      <span data-testid="card-flag" aria-hidden="true" className="block h-[2px] w-[84px] bg-accent-brand" />
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-display text-[15px] font-semibold text-fg-primary">{title}</h2>
            <p className="mt-0.5 text-xs text-fg-secondary">{blurb}</p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {stamp && (
              <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
                {stamp}
              </span>
            )}
            {/* The mock's `.dash-card__open`. Optional, because the card also
                renders on surfaces that ARE the thing it would open. */}
            {openTo && (
              <Link
                to={openTo}
                aria-label={`Open ${title}`}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-bg-border bg-bg text-fg-secondary transition hover:bg-bg-softer hover:text-fg-primary"
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
          {/* ── the rollup ── */}
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div className="flex items-baseline gap-2">
              {/* A DASH, NOT 0%. With nothing submitted there is no ratio, and
                  0% reads as failure rather than as absence. */}
              <span className="font-display text-[30px] font-semibold leading-none tracking-[-0.01em] text-fg-primary tabular-nums">
                {rollup.pct === null ? "—" : `${rollup.pct}%`}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
                approved
              </span>
            </div>

            <div className="flex h-1.5 overflow-hidden rounded-full bg-bg">
              <span className="bg-status-ok" style={{ width: `${rollup.pct ?? 0}%` }} />
              <span className="flex-1 bg-bg-softer" />
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              {cells.map((c) => (
                <div key={c.label} className="flex flex-col gap-0.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
                    {c.label}
                  </span>
                  <span className={`font-display text-[15px] font-semibold tabular-nums ${TONE[c.tone]}`}>
                    {c.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <span className="hidden w-px self-stretch bg-bg-border md:block" />

          {/* ── the queue preview ── */}
          <div data-testid="awaiting-queue" className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
              Awaiting you
            </span>
            {awaiting.length === 0 ? (
              <p className="text-xs text-fg-secondary">{emptyLine}</p>
            ) : (
              <>
                {awaiting.map((r) => (
                  <Link
                    key={r.id}
                    to="/portal/plan-reviews/$planReviewId"
                    params={{ planReviewId: r.id }}
                    className="group flex items-center gap-2 rounded-md px-1.5 py-1 transition hover:bg-bg-softer"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-action" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-xs text-fg-primary">{r.title}</span>
                      <span className="truncate font-mono text-[10px] text-fg-secondary">
                        {r.case_number}
                      </span>
                    </span>
                  </Link>
                ))}
                {more > 0 && (
                  <span className="px-1.5 text-[10px] text-fg-secondary">
                    + {more} more in the queue
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
